import { Router, ActivatedRoute, NavigationExtras} from '@angular/router';
import { DialogService, SpinnerService, Option, SaveDataRequest, DataService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS } from 'src/app/constants/api-endpoints.constants';
import { CONCESSIONS_APPLICATION_CARD_TYPE_ID, OPTION_SETS, POP_UP_CODES, QUESTION_CODES, SAVE_RESULT_STATUS, SECTION_CODES, SERVER_ERROR_POP_UP_CODES} from 'src/app/constants/app-data.constants';
import { AppDataService } from 'src/app/services/app-data.service';
import { ErrorService } from 'src/app/services/error.service';
import { BaseCardSetDetailWithHeaderComponent } from '../base-card-set-detail-with-header-component/base-card-set-detail-with-header-component';
import { ResponseParserService } from '../../services/response-parser.service';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
export abstract class ConcessionsApplicationBaseComponent extends BaseCardSetDetailWithHeaderComponent {

  protected selectedApplyForYear: string;
  protected applicationPeriod: string;
  protected previousInvoiceNumbers: any;
  protected activeDialog: string = '';
  protected id: string = '';
  protected personId: string = '';
  protected saveEndpoint: string = '';
  protected isDraftApplication: boolean = true;
  protected isCustomerPortalConcessionsApplication: boolean = false;
  protected isPersonBothCarerAndConcessionsApplicant: boolean = false;
  protected isPersonWithActiveCareAllowancePlacements: boolean = false;

  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected dataService: DataService,
    protected appDataService: AppDataService,
    protected responseParserService: ResponseParserService,
    protected dialogService: DialogService,
    protected spinnerService: SpinnerService,
    protected errorService: ErrorService
  ) {
    super(router, appDataService, responseParserService);
  }

  formLoad($event) {
    this.id = $event.id;
    this.isDraftApplication = $event.isDraftApplication;
    this.isCustomerPortalConcessionsApplication = $event.isCustomerPortalConcessionsApplication;
  }

  saveOnNavigation(templateId: number, versionNo: number) {
    if (this.isDraftApplication) {
      const saveDataRequest: SaveDataRequest = {
        template: templateId,
        versionNumber: versionNo,
        id: this.id,
        response: JSON.stringify(this.baseForm.model),
        expectedLastUpdateTime: (this.baseForm.lastUpdatedTime != null && this.baseForm.lastUpdatedTime !== '') ? this.baseForm.lastUpdatedTime : null,
        isSubmit: false
      };
      this.saveData(saveDataRequest);
    }
  }

  saveData(request: any) {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.dataService.saveData(this.saveEndpoint, {
        ...request,
        personId: this.personId
      })
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            if (SERVER_ERROR_POP_UP_CODES.saveConcessionsApplication.includes(data.message)) {
              this.activeDialog = data.message;
              this.dialogService.showDialog(this.activeDialog);
            }
            else {
              if (request.isSubmit) {
                let placeholder = {
                  message: data.message
                };
                this.activeDialog = POP_UP_CODES.concessionsApplicationCommon.submitSuccessful;
                this.dialogService.showDialog(POP_UP_CODES.concessionsApplicationCommon.submitSuccessful, null, placeholder, false);
              } else {
                this.id = data.id;
                this.baseForm.lastUpdatedTime = data.allocatedLastUpdatedTime;
                this.setPreviousModel();
              }
            }
          }
        })
    );
  }  

  showInvoiceSectionHelp() {
    this.dialogService.showDialog(POP_UP_CODES.concessionsApplicationCommon.invoiceHelpText);
  }

  updateHeaderSubtitle(selectedApplyForYearOptions: Option[]) {
    //check if year is selected
    let selectedApplyForYear = this.responseParserService.findQuestionModel(this.baseForm.model, 1, 1, null, SECTION_CODES.concessionsApplicationCommon.intro, null, QUESTION_CODES.concessionsApplicationCommon.applyForYear);

    // only run code if value has changed
    if (this.selectedApplyForYear != selectedApplyForYear) {
      this.selectedApplyForYear = selectedApplyForYear;
      if (selectedApplyForYear) {
        let selectedApplyForYearOption = selectedApplyForYearOptions.find(cs => cs.code === selectedApplyForYear);
        if (selectedApplyForYearOption) {
          let year = selectedApplyForYearOption?.tags;
          // Check if there's already a year in the subtitle
          let subtitleParts = this.formHeaderModel.subtitle.split(" - ");

          if (subtitleParts.length > 1) {
            // If there's a year, replace it with the new year
            subtitleParts.pop(); // Remove the last element (old year)
            subtitleParts.push(year); // Add the new year
            this.formHeaderModel.subtitle = subtitleParts.join(" - ");
          } else {
            // If there's no year, just append the year to the subtitle
            this.formHeaderModel.subtitle = this.formHeaderModel.subtitle + " - " + year;
          }
          this.applicationPeriod = year;
          return year;
        }
      }
    }

    return null;
  }

  parseDateString(dateString: string): Date | null {
    const parts = dateString.split('/');
    if (parts.length !== 3) {
      return null;
    }
    const [day, month, year] = parts.map(Number);
    // Months are 0-indexed in JavaScript Date, so subtract 1 from month
    return new Date(year, month - 1, day);
  }

  extractFileName(inputString): string {
    const hasPipe: boolean = inputString.includes('|');
    const filenames: string[] = [];

    if (hasPipe) {
      const properties: string[] = inputString.split('|');
      properties.forEach(property => {
        const [filename] = property.split(':');
        filenames.push(filename);
      });
    } else {
      const [filename] = inputString.split(':');
      filenames.push(filename);
    }

    return filenames.join(' | ');
  }

  recalculateInvoiceTotal(itemIndex, invoiceInfo: any, propertyName: string) {
    let formattedInvoiceTotal = "$ 0.00";
    let invoiceTotal: number = 0;

    invoiceInfo.forEach(item => {
      invoiceTotal = invoiceTotal + parseFloat(item[propertyName]);
    });

    const calculatedInvoiceTotal = invoiceTotal - invoiceInfo[itemIndex][propertyName];
    if (calculatedInvoiceTotal > 0) {
      formattedInvoiceTotal = "$" + calculatedInvoiceTotal.toFixed(2);
    }

    return formattedInvoiceTotal;
  }

  isAddressInVictoria(section: any, questionCode: string): boolean {
    if (section[questionCode + "-manualAddress"]) {
      var manualState = section[questionCode + "-state"];
      return manualState.toLowerCase() == "vic" || manualState.toLowerCase() == "victoria";
    } else {
      const autoStatQuestionCode = questionCode + "-autoState";
      return section[autoStatQuestionCode] === "VIC";
    }
  }

  public navigateBackToHome(ignoreUnsavedChanges = false): void {
    const navExtras = this.getNavigationExtras();
    this.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.list.prefix, ROUTE_CONSTANTS.concessionsApplication.list.myApplicationsSegment], ignoreUnsavedChanges, navExtras);
  }

  public navigateToConcessionsInfo(ignoreUnsavedChanges = false): void {
    this.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix], ignoreUnsavedChanges);
  }

  public getConfirmationDatesFromInvoices(lists: any[][]): string[] | null {
    // Combine all lists into one
    const combinedList = lists.reduce((accumulator, currentList) => {
      return accumulator.concat(currentList || []);
    }, []);

    // Extract invoice dates if there are any
    const invoiceDates = combinedList.map(item => item.invoiceDate).filter(date => date !== undefined && date !== null);

    // If no invoice dates are found, return null
    if (invoiceDates.length === 0) {
      return null;
    }

    // Return unique invoice dates as strings
    return invoiceDates.filter((date, index, self) => self.indexOf(date) === index).map(date => date.toString());
  }

  isValueInPreviousInvoiceNumbers(invoiceInfo: any[], itemIndex: number, value: string, invoiceType: string = null, invoiceNumber: string = 'invoiceNumber'): boolean {
    for (let i = 0; i < invoiceInfo.length; i++) {
      if (i !== itemIndex && invoiceInfo[i][invoiceNumber] === value) {
        return true;
      }
    }

    if (this.previousInvoiceNumbers) {
      const entry = this.previousInvoiceNumbers.find(item => item.applicationYear === this.selectedApplyForYear);

      if (entry) {
        let invoiceNumbers = entry.invoiceNumbers.flat(); // Flatten the nested array

        if (invoiceType) {
          invoiceNumbers = invoiceNumbers.filter(item => item.invoiceType == invoiceType)
        }
        return invoiceNumbers.some(item => item.invoiceNumber.toString() == value);
      }
    }
    return false;
  }

  protected isConsentNotAgree(): boolean {
    return this.baseForm?.model?.form?.tabs?.tab3?.page1?.cardDetails?.consent == OPTION_SETS.concessionConsentType.options.notagree || this.baseForm?.model?.form?.tabs?.tab3?.page1?.cardDetails?.consent == OPTION_SETS.concessionConsentType.options.notSignedLetterProvided;
  }

  protected isConsentAgree(): boolean {
    return this.baseForm?.model?.form?.tabs?.tab3?.page1?.cardDetails?.consent == OPTION_SETS.concessionConsentType.options.signed || this.baseForm?.model?.form?.tabs?.tab3?.page1?.cardDetails?.consent == OPTION_SETS.concessionConsentType.options.agree;
  }

  protected isConsentNotsignedandNoConfirmation(): boolean {
    return this.baseForm?.model?.form?.tabs?.tab3?.page1?.cardDetails?.consent == OPTION_SETS.concessionConsentType.options.notSignedNoConfirmation;
  }

  protected showSignedApplicationUpload(): boolean {
    return this.baseForm?.model?.form?.tabs?.tab3?.page1?.cardDetails?.consent == OPTION_SETS.concessionConsentType.options.signed;
  }

  protected isReadOnly() {
    return !this.isDraftApplication;
  }

  protected isStaffPortalApplication() {
    return !this.isCustomerPortalConcessionsApplication;
  }

  protected addNewPostalAddress() {
    return this.baseForm?.model?.form?.tabs?.tab2?.page1?.address?.postalAddressSameAsPrimaryAddress == OPTION_SETS.postalAddressSameOrDifferent.options.addNewPostalAddress;
  }

  protected isCustomerPortalApplication() {
    return this.isCustomerPortalConcessionsApplication;
  }

  protected checkIfAnyInvoiceIsInEditMode(...lists) {
    for (const list of lists) {
      if (list != null && list.some(item => item.isEditing)) {
        return true;
      }
    }
    return false;
  }

  protected checkSupportingDocCheckboxSelectedOnInvoice(documentInfo: any) {
    return (documentInfo?.supportingDoc == true);
  }

  protected getNavigationExtras(): NavigationExtras {
    return {
      state: {
        applicationFilterRequest: history.state.applicationFilterRequest
      }
    };
  }

  protected isFirstNameReadonly(): boolean {
    return this.isPersonBothCarerAndConcessionsApplicantWithActivePlacements();
  }

  protected isLastNameReadonly(): boolean {
    return this.isPersonBothCarerAndConcessionsApplicantWithActivePlacements();
  }

  protected isEmailReadonly() {
    return this.isPersonBothCarerAndConcessionsApplicantWithActivePlacements();
  }

  protected isMobilePhoneReadonly() {
    return this.isPersonBothCarerAndConcessionsApplicantWithActivePlacements();
  }

  protected isOtherPhoneReadonly() {
    return this.isPersonBothCarerAndConcessionsApplicantWithActivePlacements();
  }

  protected isDateOfBirthReadonly() {
    return this.isPersonBothCarerAndConcessionsApplicantWithActivePlacements();
  }

  protected isPrimaryAddressReadonly() {
    return this.isPersonBothCarerAndConcessionsApplicantWithActivePlacements();
  }

  protected isPostalAddressSameAsPrimaryAddressReadonly() {
    return this.isPersonBothCarerAndConcessionsApplicantWithActivePlacements();
  }

  protected isPostalAddressReadonly() {
    return this.isPersonBothCarerAndConcessionsApplicantWithActivePlacements();
  }

  protected isPersonBothCarerAndConcessionsApplicantWithActivePlacements(): boolean {
    if (this.isPersonBothCarerAndConcessionsApplicant) {
      return this.isPersonWithActiveCareAllowancePlacements;
    }
    return false;
  }

  // E.g. 100.1 will get converted to 100.10
  protected getInvoiceAmountWithTwoDecimalPlaces(invoiceAmount: string): string {
    const num = parseFloat(invoiceAmount);

    // Round it to 2 decimal places
    const rounded = Math.round(num * 100) / 100;

    // Format with exactly two decimal places
    return rounded.toFixed(2);
  }
}
